import React, { useContext, useState, useEffect } from "react";
import "./CSS/ShopCategory.css";
import { ShopContext } from "../Context/ShopContext";
import search_icon from "../Components/Assets/search.svg";
import Item from "../Components/Items/Item";
import default_img from "../Components/Assets/default_img.png";
import PageLoader from "../Components/Assets/PageLoader/PageLoader";

const mainURL = process.env.REACT_APP_API_BASE_URL;

const ShopCategory = (props) => {
  const { all_products, loading } = useContext(ShopContext);
  const { category, banner } = props;

  // --- STATE ---
  const [sortType, setSortType] = useState("default");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [productsPerPage] = useState(20);

  // Reset to page 1 whenever category or search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [category, searchTerm]);

  if (loading) return <PageLoader text="Loading products..." />;

  // --- 1. FILTERING LOGIC ---
  let filteredProducts = all_products.filter(
    (item) => Number(item.item_category_id) === Number(category)
  );

  if (searchTerm) {
    filteredProducts = filteredProducts.filter((item) =>
      item.item_name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }

  // --- 2. SORTING LOGIC ---
  const sortedProducts = [...filteredProducts].sort((a, b) => {
    const priceA = a.product_cost?.[0]?.selling_price ?? 0;
    const priceB = b.product_cost?.[0]?.selling_price ?? 0;
    const nameA = a.item_name.toLowerCase();
    const nameB = b.item_name.toLowerCase();

    switch (sortType) {
      case "price-low-high": return priceA - priceB;
      case "price-high-low": return priceB - priceA;
      case "name-a-z": return nameA.localeCompare(nameB);
      case "name-z-a": return nameB.localeCompare(nameA);
      case "newest": return b.item_id - a.item_id;
      default: return 0;
    }
  });

  // --- 3. PAGINATION CALCULATION ---
  const totalProducts = sortedProducts.length;
  const totalPages = Math.ceil(totalProducts / productsPerPage);
  
  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  
  // These are the products actually shown on the screen
  const currentDisplayedProducts = sortedProducts.slice(indexOfFirstProduct, indexOfLastProduct);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return ( 
    <div className="shop-category">
      {banner && <img className="shopcategory-banner" src={banner} alt="Category Banner" />}

      <div className="shopcategory-controls">
        <div className="shopcategory-search-container">
          <div className="search-wrapper">
            <input
              type="text"
              className="highlighted-search"
              placeholder="Search products in this category..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button className="search-inside-btn">
              <img src={search_icon} alt="Search" className="inner-search-icon" />
            </button>
          </div>
        </div>

        <div className="shopcategory-indexSort">
          <p>
            Showing <span>{indexOfFirstProduct + 1}-{Math.min(indexOfLastProduct, totalProducts)}</span> out of <span>{totalProducts}</span> products
          </p>
          <div className="shopcategory-sort-container">
            <select
              className="shopcategory-sort-select"
              onChange={(e) => setSortType(e.target.value)}
            >
              <option value="default">Sort by: Relevant</option>
              <option value="newest">Newest Arrivals</option>
              <option value="price-low-high">Price: Low to High</option>
              <option value="price-high-low">Price: High to Low</option>
              <option value="name-a-z">Name: A - Z</option>
              <option value="name-z-a">Name: Z - A</option>
            </select>
          </div>
        </div>
      </div>

      {totalProducts === 0 ? (
        <div className="no-product-container">
          <p>No products match your search "{searchTerm}"</p>
          <button onClick={() => setSearchTerm("")} className="clear-btn">Clear Search</button>
        </div>
      ) : (
        <>
          <div className="shopcategory-products">
            {currentDisplayedProducts.map((item) => {
              const imagePath = item?.product_images?.[0]?.image_path
                ? `${mainURL}/${item.product_images[0].image_path}`
                : default_img;

              return (
                <Item
                  key={item.item_id}
                  id={item.item_id}
                  name={item.item_name}
                  image={imagePath}
                  is_wishlist={item.is_wishlist}
                  new_price={item?.product_cost?.[0]?.selling_price ?? 0}
                  old_price={item?.product_cost?.[0]?.mrp ?? item?.product_cost?.[0]?.selling_price}
                />
              );
            })}
          </div>

          {/* --- PAGINATION UI --- */}
          {totalPages > 1 && (
            <div className="pagination-container">
              <button 
                disabled={currentPage === 1} 
                onClick={() => paginate(currentPage - 1)}
                className="pagination-arrow"
              >
                Previous
              </button>

              {[...Array(totalPages)].map((_, index) => (
                <button
                  key={index + 1}
                  onClick={() => paginate(index + 1)}
                  className={currentPage === index + 1 ? "pagination-item active" : "pagination-item"}
                >
                  {index + 1}
                </button>
              ))}

              <button 
                disabled={currentPage === totalPages} 
                onClick={() => paginate(currentPage + 1)}
                className="pagination-arrow"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  ); 
};

export default ShopCategory;